import React, { useState, useEffect, useRef } from "react";
import { apiRequest } from "@ridecloud/shared";
import { devError } from "@ridecloud/shared";

function SignupOtp({ email, onSuccess, onBack }) {
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [timer, setTimer] = useState(120);
  const [message, setMessage] = useState("");
  const inputRefs = useRef([]);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const handleOtpChange = (index, value) => {
    if (value.length <= 1) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 3) inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length !== 4) return setMessage("Please enter all 4 digits");
    setLoading(true);
    setMessage("");
    try {
      await apiRequest(async (APIv1) =>
        APIv1.post(
          "/auth/verify-signup-otp",
          { email, otp: otpValue },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
      );
      onSuccess();
    } catch (error) {
      setMessage(
        error.response?.data?.message ||
          "Invalid verification code. Please try again."
      );
      devError("OTP verification error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResendLoading(true);
    setMessage("");
    try {
      await apiRequest(async (APIv1) =>
        APIv1.post(
          "/auth/resend-otp",
          { email },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
      );

      setTimer(120);
      setOtp(["", "", "", ""]);
      setMessage("Verification code sent successfully!");

      inputRefs.current[0]?.focus();

      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      setMessage(
        error.response?.data?.message ||
          "Failed to resend verification code. Please try again."
      );
      devError("Resend OTP error:", error);
    } finally {
      setResendLoading(false);
    }
  };

  const formatTimer = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 relative flex items-center justify-center p-4 sm:p-6 overflow-hidden">
      {/* Background decorations - responsive positioning */}
      <div className="absolute w-64 h-64 sm:w-96 sm:h-96 bg-gradient-to-r from-blue-400 to-indigo-500 top-10 -left-20 sm:-left-48 blur-3xl rounded-full opacity-20"></div>
      <div className="absolute w-64 h-64 sm:w-80 sm:h-80 bg-gradient-to-r from-purple-400 to-pink-500 bottom-10 -right-20 sm:-right-40 blur-3xl rounded-full opacity-20"></div>

      <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-6 sm:p-8 w-full max-w-md relative z-10">
        <div className="text-center mb-6 sm:mb-8">
          <div className="flex justify-center mb-4 sm:mb-6">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-700 rounded-2xl flex items-center justify-center">
              <svg
                className="w-6 h-6 sm:w-8 sm:h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
            Verify Your Email
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mb-1">
            We've sent a 4-digit verification code to
          </p>
          <p className="text-sm sm:text-base font-semibold text-indigo-600 break-all">
            {email}
          </p>
        </div>

        <form onSubmit={handleVerify}>
          <div className="flex justify-center gap-3 sm:gap-4 mb-4 sm:mb-6">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                className="w-12 h-12 sm:w-14 sm:h-14 text-center text-xl sm:text-2xl font-bold border-2 border-gray-200 rounded-lg sm:rounded-xl 
                         focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/20 focus:outline-none
                         transition-all duration-200 bg-white/50"
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
              />
            ))}
          </div>

          {message && (
            <div
              className={`flex items-center justify-center gap-2 p-3 rounded-lg sm:rounded-xl mb-3 sm:mb-4 text-sm sm:text-base text-center ${
                message.includes("successfully")
                  ? "text-green-600 bg-green-50"
                  : "text-red-600 bg-red-50"
              }`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {message.includes("successfully") ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                )}
              </svg>
              <span>{message}</span>
            </div>
          )}

          <div className="mb-4 sm:mb-6">
            <button
              type="submit"
              className="w-full bg-emerald-500 text-white font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-lg sm:rounded-xl
           hover:bg-emerald-600 focus:ring-4 focus:ring-emerald-500/30 focus:outline-none
           transition-all duration-200 disabled:bg-gray-300 disabled:cursor-not-allowed
           flex items-center justify-center gap-2 text-sm sm:text-base"
              disabled={loading || timer === 0}
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Verifying...
                </div>
              ) : (
                <>
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>Verify Code</span>
                </>
              )}
            </button>
          </div>

          <div className="space-y-3 sm:space-y-4">
            <p className="text-center text-xs sm:text-sm text-gray-600">
              Didn't receive the code?
            </p>

            <button
              type="button"
              onClick={handleResendOtp}
              className={`w-full font-medium py-2 sm:py-3 px-3 sm:px-4 rounded-lg sm:rounded-xl transition-all duration-200
                         flex items-center justify-center gap-2 border-2 text-xs sm:text-sm ${
                           timer === 0
                             ? "text-indigo-600 bg-indigo-50 border-indigo-200 hover:bg-indigo-100 hover:border-indigo-300"
                             : "text-gray-400 bg-gray-50 border-gray-200 cursor-not-allowed"
                         }`}
              disabled={timer > 0 || resendLoading}
            >
              {resendLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-indigo-600/30 border-t-indigo-600 rounded-full animate-spin"></div>
                  Sending...
                </div>
              ) : timer > 0 ? (
                <>
                  <svg
                    className="w-3 h-3 sm:w-4 sm:h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  <span>Resend in {formatTimer(timer)}</span>
                </>
              ) : (
                <>
                  <svg
                    className="w-3 h-3 sm:w-4 sm:h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  <span>Resend Code</span>
                </>
              )}
            </button>

            <div className="text-center">
              <span
                onClick={onBack}
                className="text-xs sm:text-sm text-gray-500 hover:text-indigo-600 cursor-pointer transition-colors duration-200
                         flex items-center justify-center gap-1"
              >
                <svg
                  className="w-3 h-3 sm:w-4 sm:h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                Go back
              </span>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SignupOtp;
